import classNames from "classnames";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import iconMinus from "../../../../img/icons/minus.svg";
import iconPlus from "../../../../img/icons/plus.svg";
import { getProductByUUID } from "../../../api/products";
import { Link } from "../../../common/Link";
import { RatingGraphic } from "../../../common/RatingGraphic";
import { IWebPageURL } from "../../../models/nominals";
import { IReview } from "../../../models/reviews";
import { formatDateString } from "../../../utils/format";

const buildProductTitle = (
    review: IReview,
    enablePDPLinks: boolean,
    productLink: IWebPageURL | null,
): React.ReactNode => {
    const title = review.product_variant_display_name
        ? `${review.product_name} – ${review.product_variant_display_name}`
        : review.product_name;
    if (enablePDPLinks && productLink) {
        return (
            <Link
                className="customer-review__review-item__product-link"
                href={productLink}
            >
                {title}
            </Link>
        );
    }
    return <>{title}</>;
};

const buildToggleButton = (
    isExpanded: boolean,
    currentReviewElement: React.RefObject<HTMLButtonElement | null>,
    clickToExpand: () => void,
): React.ReactNode => {
    return (
        <button
            ref={currentReviewElement}
            onClick={clickToExpand}
            aria-controls="toggle-review"
            aria-expanded={isExpanded}
        >
            {isExpanded ? (
                <span>
                    <SVG aria-hidden="true" src={iconMinus} />
                    {t`View Less`}
                </span>
            ) : (
                <span>
                    <SVG aria-hidden="true" src={iconPlus} />
                    {t`View More`}
                </span>
            )}
        </button>
    );
};

export const CarouselReview = (props: {
    review: IReview;
    showSideDate: boolean;
    enablePDPLinks: boolean;
    isMobile: boolean;
    closeAllReviewColumns: boolean;
    starHasStroke?: boolean;
    expandReviewColumn: (isExpanded: boolean) => void;
    currentReviewElement: React.RefObject<HTMLButtonElement | null>;
    expandContentThreshold: number;
}): React.ReactNode => {
    const reviewContentRef = React.useRef<HTMLDivElement>(null);
    const reviewCopyRef = React.useRef<HTMLDivElement>(null);
    const didMountRef = React.useRef(false);

    const [productLink, setProductLink] = React.useState<IWebPageURL | null>(
        null,
    );
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [reviewCopyHeight, setReviewCopyHeight] = React.useState(0);

    const checkReviewCopyHeight = () => {
        if (reviewCopyRef.current) {
            setReviewCopyHeight(reviewCopyRef.current.offsetHeight);
        }
    };

    const clickToExpand = () => {
        props.expandReviewColumn(isExpanded);
        setIsExpanded(!isExpanded);
    };

    // Mirror componentDidMount logic: bootstrap listeners + fetch product link
    React.useEffect(() => {
        const window = getWindow();
        const shouldAttachResizeListener = Boolean(window) && props.isMobile;

        if (shouldAttachResizeListener && window) {
            checkReviewCopyHeight();
            window.addEventListener("resize", checkReviewCopyHeight);
        }

        const productUuid = props.review.product_uuid;
        if (productUuid) {
            const fetchProduct = async () => {
                const product = await getProductByUUID(productUuid);
                if (product && product.link && product.show_in_site_search) {
                    setProductLink(product.link);
                }
            };
            void fetchProduct();
        }

        return () => {
            if (shouldAttachResizeListener && window) {
                window.removeEventListener("resize", checkReviewCopyHeight);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Mirror componentDidUpdate behavior for collapse + mobile measurement
    React.useEffect((): void => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }

        if (props.closeAllReviewColumns) {
            setIsExpanded(false);
        }

        if (props.isMobile) {
            checkReviewCopyHeight();
        }
    });

    const displayTogglebutton =
        props.isMobile && reviewCopyHeight >= props.expandContentThreshold;
    const formattedDate = formatDateString(
        props.review.created_datetime,
        "MMM d, yyyy",
    );
    const formattedReviewDate = props.showSideDate ? formattedDate : null;

    const content = props.review.highlighted
        ? props.review.highlighted
        : props.review.text;

    const contentClasses = classNames({
        "customer-review__review-content": true,
        "customer-review__review-content--expanded": isExpanded,
        "customer-review__review-content--no-expand-button":
            !displayTogglebutton,
    });
    const isSelected =
        reviewContentRef &&
        reviewContentRef.current?.parentElement?.parentElement?.classList.contains(
            "is-selected",
        );
    const tabIndex =
        isSelected &&
        !props.isMobile &&
        reviewCopyRef.current &&
        reviewCopyRef.current.offsetHeight >= props.expandContentThreshold
            ? 0
            : -1;

    return (
        <div
            className="customer-review__review-item"
            role="group"
            aria-label={t`Product Review`}
        >
            <div className="customer-review__review-content-wrapper">
                {buildProductTitle(
                    props.review,
                    props.enablePDPLinks,
                    productLink,
                )}
                <RatingGraphic
                    cardClass="product-reviews-filter"
                    cardSize="standard"
                    rating={props.review.rating}
                    starHasStroke={props.starHasStroke}
                />
                <div
                    ref={reviewContentRef}
                    className={contentClasses}
                    tabIndex={tabIndex}
                >
                    <strong>{props.review.headline}</strong>
                    <p
                        ref={reviewCopyRef}
                        id={displayTogglebutton ? "toggle-review" : ""}
                        dangerouslySetInnerHTML={{ __html: content }}
                    ></p>
                </div>
                {displayTogglebutton &&
                    buildToggleButton(
                        isExpanded,
                        props.currentReviewElement,
                        clickToExpand,
                    )}
                <div className="customer-review__reivew-user-info">
                    <strong title={props.review.name}>
                        {props.review.name}
                    </strong>
                    <div
                        title={`${props.review.location}, ${formattedReviewDate}`}
                    >
                        <span>{props.review.location}</span>
                        <span>{formattedReviewDate}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
